<template>
    <div class="slide-deck-corporate">
        <!-- The slide player is the full-width hero. -->
        <div class="slide-deck-shell">
            <activity-generating-placeholder
                v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)"
                activity-type="slide-deck"
                :generation-status="generationStatus"
                :generation-error="generationError"
            />

            <slide-deck-player
                v-else-if="slideDeck"
                :slide-deck-id="slideDeck?.id"
                filmstrip-thumbnails
                track-view-progress
                :completed="completed"
                @viewed-change="onViewedChange"
            />

            <div v-else-if="showSlideDeckSkeleton" class="slide-deck-skeleton" aria-live="polite" aria-busy="true">
                <div class="slide-deck-skeleton__frame" />
                <div class="slide-deck-skeleton__thumbs">
                    <div v-for="index in 6" :key="index" class="slide-deck-skeleton__thumb" />
                </div>
            </div>

            <article v-else class="message is-warning">
                <div class="message-body">No Slide Deck linked to this activity.</div>
            </article>
        </div>

        <!-- Content + acknowledgment + action share ONE measure so right edges align. -->
        <div class="slide-deck-corporate-measure">
            <!-- Slide-deck notes / description, shown beneath the player (the deck's
                 own `activity_content.content`). Rendered via the shared DisplayContent
                 renderer, mirroring the other corporate activity variants. -->
            <display-content
                v-if="activityContent && activityContent.content"
                class="slide-deck-corporate-content"
                :content="activityContent.content"
            />

            <div v-if="activity.activity_settings.acknowledgment && !readOnly" class="slide-deck-corporate-ack">
                <h2 class="slide-deck-corporate-heading">{{ translate('acknowledgment') }}</h2>
                <e-checkbox v-if="!completed" v-model="userAccepted">
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>
                <!-- eslint-disable-next-line vue/prefer-true-attribute-shorthand -->
                <e-checkbox v-else disabled model-value>
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>
            </div>

            <activity-completion-action-row class="slide-deck-corporate-action">
                <e-button
                    v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                    :loading="isLoading"
                    :disabled="disable"
                    type="is-primary"
                    @click="submit"
                    @keyup="submit"
                >
                    {{ translate('mark_complete') }}
                </e-button>
            </activity-completion-action-row>
        </div>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import ActivityGeneratingPlaceholder from '@/components/activity-types/course-activity-types/activities/shared/ActivityGeneratingPlaceholder.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import SlideDeckPlayer from '@/components/slide-decks/SlideDeck.vue';
import { useActivity } from '@/composables/useActivity.js';
import { useSlideDeckActivity } from '@/composables/useSlideDeckActivity.js';

/**
 * Corporate presentation of the Slide Deck activity.
 *
 * Shared behaviour comes from `useActivity`; the slide-deck-specific fetch +
 * generation polling comes from `useSlideDeckActivity`. The slide player and the
 * generating placeholder are the existing reusable child components. Stanbridge
 * `SlideDeck.vue` is unchanged; the dispatcher selects this variant for the
 * corporate client via CORPORATE_RENDERER_OVERRIDES. The slide-deck DOWNLOAD is
 * handled by the corporate shell header, not here.
 */
export default {
    name: 'SlideDeckCorporate',

    components: {
        ActivityCompletionActionRow,
        ActivityGeneratingPlaceholder,
        SlideDeckPlayer,
        DisplayContent,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });
        const deck = useSlideDeckActivity(props);

        return { translate, ...api, ...deck };
    },

    data: () => ({
        userAccepted: false,
        // Mark Complete stays disabled until the learner has viewed every slide in the deck.
        // Driven by the player's `viewed-change` event; persisted server-side so it survives reloads.
        allSlidesViewed: false,
    }),

    computed: {
        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: { acknowledgment: this.userAccepted },
            };
        },

        // Slide deck adds an optional acknowledgment gate on top of the shared `disabled`.
        disable() {
            // Gate completion until every slide has been viewed — but only while actively working
            // through the deck. Once completed (or in read-only review), defer to the base state.
            if (!this.completed && !this.readOnly && this.slideDeck && !this.allSlidesViewed) {
                return true;
            }

            if (this.activity.activity_settings.acknowledgment) {
                return this.disabled || !this.userAccepted;
            }

            return this.disabled;
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },
    },

    methods: {
        onViewedChange({ allViewed }) {
            this.allSlidesViewed = allViewed;
        },
    },
};
</script>

<style scoped lang="scss">
.slide-deck-corporate {
    // The corporate shell section owns the 860px content measure (Figma 17111:44499),
    // left-aligned within the page; fill it so the player sits at the same width as the
    // status banners and the rest of the activity content.
    // Allow this column to shrink below the filmstrip's intrinsic width so the page
    // stays responsive (the filmstrip scrolls instead of forcing the layout wider).
    min-width: 0;

    .slide-deck-shell,
    .slide-deck-corporate-measure {
        min-width: 0;
    }

    .slide-deck-corporate-measure {
        margin-top: 1.5rem;
    }

    .slide-deck-corporate-ack {
        margin-top: 1.5rem;
    }

    .slide-deck-corporate-heading {
        color: #363636;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
    }

    // Corporate restyle of the shared player to match Figma 17111:44481: rounded
    // slide frame, blue circular prev/next arrows, blue rounded-square fullscreen
    // toggle, rounded thumbnails. Scoped to the corporate variant via :deep so the
    // shared player + stanbridge are unaffected.
    .slide-deck-shell {
        // Slide image: SQUARE corners, full-bleed cover (Figma 17111:44499 — the image
        // rect is square; only the surrounding card is rounded). The player's slide-ratio
        // drives the height.
        :deep(.vueperslides:not(.thumbnails) .vueperslides__parallax-wrapper) {
            overflow: hidden;
        }

        :deep(.vueperslides:not(.thumbnails) .vueperslide) {
            background-size: cover;
        }

        // Prev/next: blue VERTICAL PILLS (not circles) with a white caret — Figma spec
        // 37x59px, radius ~4px, fill #3b82f6, no shadow.
        :deep(.vueperslides__arrow) {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 37px;
            height: 59px;
            color: #fff;
            background: var(--primary-color);
            border-radius: 4px;
            box-shadow: none;

            // The built-in vueperslides chevron <svg> renders unreliably once the arrow
            // is restyled (its drawing viewport collapses). Hide it and draw the caret as
            // a rotated CSS border-square instead — dependency-free and always paints.
            &::before {
                display: none;
            }

            svg {
                display: none;
            }

            &::after {
                content: '';
                display: block;
                width: 10px;
                height: 10px;
                border-top: 2px solid #fff;
                border-right: 2px solid #fff;
            }

            &:hover {
                filter: brightness(0.93);
            }
        }

        // Point the caret: prev = left chevron, next = right chevron. The small offset
        // optically centres the rotated square within the pill.
        :deep(.vueperslides__arrow--prev::after) {
            transform: rotate(-135deg);
            margin-left: 4px;
        }

        :deep(.vueperslides__arrow--next::after) {
            transform: rotate(45deg);
            margin-right: 4px;
        }

        // Fullscreen toggle: blue rounded square, radius 6 (Figma).
        :deep(.fullscreen-icon) {
            background: var(--primary-color);
            color: #fff;
            border-radius: 6px;
        }

        // Thumbnails are rendered as the left-aligned filmstrip (filmstrip-thumbnails
        // prop on the player), styled in SlideDeck.vue — no vueperslides thumbnail
        // slider overrides needed here.
    }
}

// Loading skeleton (mirrors the stanbridge SlideDeck shimmer).
.slide-deck-skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.slide-deck-skeleton__frame,
.slide-deck-skeleton__thumb {
    position: relative;
    overflow: hidden;
    background: #e5e7eb;
    border-radius: 8px;
}

.slide-deck-skeleton__frame {
    width: 100%;
    aspect-ratio: 16 / 9;
}

.slide-deck-skeleton__thumbs {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 8px;
}

.slide-deck-skeleton__thumb {
    width: 100%;
    aspect-ratio: 1 / 1;
}

.slide-deck-skeleton__frame::after,
.slide-deck-skeleton__thumb::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    animation: slideDeckCorporateSkeletonShimmer 1.6s infinite;
}

@keyframes slideDeckCorporateSkeletonShimmer {
    100% {
        transform: translateX(100%);
    }
}
</style>
